import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/services/api';
import { Mail, Loader2, Check, AlertTriangle, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ForgotPasswordPage() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [step, setStep] = useState<'request' | 'sent'>('request');
    const [error, setError] = useState('');

    const requestMutation = useMutation({
        mutationFn: () => api.requestPasswordReset(email),
        onSuccess: () => {
            setStep('sent');
        },
        onError: (err: Error) => {
            setError(err.message);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (email) {
            setError('');
            requestMutation.mutate();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 px-4">
            <div className="w-full max-w-md">
                <div className="card p-8">
                    {/* Request Form */}
                    {step === 'request' && (
                        <>
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <KeyRound className="w-8 h-8 text-primary-600" />
                                </div>
                                <h1 className="text-2xl font-bold mb-2">
                                    {t('auth.resetPassword.title', 'Reset Password')}
                                </h1>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    {t('auth.resetPassword.subtitle', 'Enter your email to receive a reset link')}
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="label">{t('auth.email', 'Email')}</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input"
                                        placeholder="you@example.com"
                                        required
                                    />
                                </div>

                                {error && (
                                    <div className="p-3 bg-danger-50 dark:bg-danger-600/20 text-danger-600 text-sm rounded-lg flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={requestMutation.isPending}
                                    className="btn-primary w-full"
                                >
                                    {requestMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Mail className="w-4 h-4" />
                                            {t('auth.resetPassword.sendLink', 'Send Reset Link')}
                                        </>
                                    )}
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link to="/login" className="text-sm text-neutral-500 hover:text-neutral-700">
                                    {t('twoFactor.backToLogin', '← Back to login')}
                                </Link>
                            </div>
                        </>
                    )}

                    {/* Email Sent */}
                    {step === 'sent' && (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-success-600" />
                            </div>
                            <h1 className="text-2xl font-bold mb-2">
                                {t('auth.resetPassword.checkEmail', 'Check Your Email')}
                            </h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                {t('auth.resetPassword.emailSent', 'If an account exists with this email, we\'ve sent a password reset link.')}
                            </p>
                            <p className="text-sm text-neutral-500 mb-6">
                                {t('auth.resetPassword.linkExpiry', 'The link will expire in 1 hour.')}
                            </p>
                            <Link to="/login" className="btn-primary inline-flex">
                                {t('twoFactor.backToLogin', '← Back to login')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
